window.CollectionHelper = class {

	/**
	 * @constructor
	 * @param {string|null} primary_key - La clave primaria utilizada para indexar los datos en la colección.
	 * @param {array|object|null} values - Datos iniciales para cargar en la colección. Puede ser un array, un objeto o una URL.
	 * @throws {Error} Si no se proporciona la clave primaria.
	 */
	constructor(primary_key = null, values = null) {
		if (primary_key == null) {
			throw new Error('primary key not provided');
		}

		this.primary_key = primary_key;

		if (values != null) this.load(values); // Carga datos si se proporcionan
	}

	/**
	 * @description Carga la colección con contenido desde un array, un objeto, o una URL.
	 * @param {*} input - Los datos a cargar, que pueden ser un array, un objeto, o una URL para obtener datos.
	 * @returns {Promise<array>} - Devuelve el array de datos cargado.
	 * @throws {Error} Si el tipo de datos no es reconocido.
	 */
	async load(input) {
		this.array = []; // Inicializa el array para almacenar los datos
		this.object = {}; // Inicializa el objeto para indexar los datos por la clave primaria

		if (typeof(input) === 'string') {
			// Si el input es una URL, realiza una solicitud GET para obtener los datos
			const response = await axios.get(input);
			input = response.data;
		}

		if (Array.isArray(input)) {
			this.array = input;
			// Convierte el array a un objeto indexado por la clave primaria
			for (let [key, value] of Object.entries(input)) {
				this.object[value[this.primary_key]] = value;
			}
		} else if (typeof(input) === 'object') {
			this.object = input;
			// Convierte el objeto a un array de valores
			for (const [key, value] of Object.entries(input)) {
				this.array.push(value);
			}
		} else {
			throw new Error('unknown data type ' + typeof(input));
		}

		return this.array;
	}

	/**
	 * @description Devuelve el objeto de datos de la colección.
	 * @returns {object} - El objeto de datos.
	 */
	getObject() {
		return this.object;
	}

	/**
	 * @description Devuelve el array de datos de la colección.
	 * @returns {array} - El array de datos.
	 */
	getArray() {
		return this.array;
	}

	/**
	 * @description Devuelve el array de datos ordenado por la propiedad 'text'.
	 * @returns {array} - El array de datos ordenado.
	 */
	getArraySorted() {
		return this.getArray().sort((a, b) => {
			return a.text < b.text ? -1 : a.text > b.text ? 1 : 0;
		});
	}

	/**
	 * @description Devuelve una lista de elementos cuyo claves están en el array de claves proporcionado.
	 * @param {array} keys - Array de claves para buscar en el objeto de datos.
	 * @returns {array} - El array de elementos encontrados.
	 */
	getAll(keys) {
		let tmp = [];
		for (let i in keys) {
			tmp.push(this.object[keys[i]]);
		}
		return tmp;
	}

	/**
	 * @description Obtiene un elemento del objeto de datos por su clave primaria. También puede obtener un subvalor si se proporciona un subkey.
	 * @param {string} key - La clave del objeto de datos.
	 * @param {string|null} subkey - La subclave dentro del objeto, opcional.
	 * @returns {*} - El valor del objeto de datos o una cadena vacía si no se encuentra.
	 */
	get(key, subkey = null) {
		let tmp = this.object[key];
		if (typeof tmp === 'object' && subkey != null) {
			return tmp[subkey];
		}
		return tmp !== undefined ? tmp : '';
	}

	/**
	 * @description Busca un elemento en el array de datos por una clave y valor especificados.
	 * @param {string} key - La clave para buscar en el array de datos.
	 * @param {*} value - El valor que se está buscando.
	 * @returns {object|null} - El objeto encontrado o null si no se encuentra.
	 */
	find(key, value) {
		for (let i in this.array) {
			if (this.array[i][key] == value) {
				return this.array[i];
			}
		}
		return null;
	}

	/**
	 * @description Ordena el array de datos por una clave especificada. Puede ser en orden ascendente o descendente.
	 * @param {string} key - La clave por la que se va a ordenar.
	 * @param {boolean} [increasing=true] - Si true, ordena en orden ascendente; si false, en orden descendente.
	 * @returns {array} - El array de datos ordenado.
	 */
	sort(key, increasing = true) {
		return this.getArray().sort((a, b) => {
			if (increasing) {
				return a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0;
			} else {
				return a[key] < b[key] ? 1 : a[key] > b[key] ? -1 : 0;
			}
		});
	}

	/**
	 * @description Filtra los datos del array para devolver una lista que coincide con la clave y el valor especificados. Puede ordenar los resultados por 'text'.
	 * @param {string} key - La clave para filtrar los datos.
	 * @param {*} value - El valor que debe coincidir.
	 * @param {boolean} [sort=false] - Si true, ordena los resultados por 'text'.
	 * @returns {array} - El array filtrado.
	 */
	filter(key, value, sort = false) {
		let tmp = [];
		for (let i in this.array) {
			if (this.array[i][key] == value) {
				tmp.push(this.array[i]);
			}
		}

		if (sort) {
			tmp = tmp.sort((a, b) => {
				return a.text < b.text ? -1 : a.text > b.text ? 1 : 0;
			});
		}

		return tmp;
	}

	/**
	 * @description Genera una lista en formato 'select2' con objetos que coinciden con el valor proporcionado para 'db_key'.
	 * @param {*} value - El valor para 'db_key' que debe coincidir.
	 * @returns {array} - El array de objetos formateados para 'select2'.
	 */
	select2(value) {
		let tmp = [];
		for (let obj of this.array) {
			if (obj['db_key'] == value) {
				// Crea una copia del objeto y añade propiedades adicionales para 'select2'
				tmp.push(Object.assign({}, obj, { name: obj.text, text: obj.category + ' ' + obj.text }));
			}
		}
		return tmp.sort((a, b) => {
			return a.text < b.text ? -1 : a.text > b.text ? 1 : 0;
		});
	}

	/**
	 * @description Agrupa los datos del array por una clave especificada.
	 * Ideal para agrupar datos por una categoría específica.
	 * @param {string} key - La clave por la que se va a agrupar.
	 * @returns {array} - Un array de arrays agrupados por la clave.
	 */
	group(key) {
		let tmp = [];
		for (let item of this.array) {
			// Si no existe el grupo, lo crea
			if (!Array.isArray(tmp[item[key]])) {
				tmp[item[key]] = [];
			}
			tmp[item[key]].push(item); // Añade el item al grupo correspondiente
		}
		return tmp;
	}

	/**
	 * @description Establece un nuevo valor en el objeto de datos y, si la clave no existe, agrega el valor al array.
	 * @param {string} key - La clave para el valor a establecer.
	 * @param {*} value - El valor a establecer en el objeto de datos.
	 */
	set(key, value) {
		if (!(key in this.object)) {
			this.array.push(value); // Agrega el valor al array si no existe en el objeto
		}
		this.object[key] = value; // Establece el valor en el objeto
	}

	/**
	 * @description Asigna valores adicionales a un elemento existente en el objeto de datos. Si el elemento no existe, lo agrega.
	 * @param {string} key - La clave del objeto de datos.
	 * @param {*} value - Los valores a asignar al objeto.
	 */
	assign(key, value) {
		if (!(key in this.object)) {
			this.set(key, value); // Si no existe, lo establece
		} else {
			Object.assign(this.object[key], value); // Asigna valores adicionales
		}
	}

	/**
	 * @description Elimina un elemento del objeto de datos y del array basado en la clave proporcionada.
	 * @param {string} key - La clave del elemento a eliminar.
	 * @param {string|null} [value=null] - Valor opcional para eliminar un elemento específico en el array.
	 */
	remove(key, keyname = null) {
		keyname = (keyname == null) ? this.primary_key : keyname;

		// Verifica si la clave existe en el objeto
		if (key in this.object) {
			// Elimina del objeto
			delete this.object[key];

			// Encuentra el índice del elemento en el array
			const index = this.array.findIndex(item => item[keyname] == key);

			// Si el índice es válido, elimina el elemento del array
			if (index !== -1) {
				this.array.splice(index, 1);
			}
		}
	}

	/**
	 * @description Establece una lista completa de valores en el array de datos y el objeto de datos.
	 * @param {array} array - El array de valores para establecer.
	 */
	setArray(array) {
		this.array = array;
		// Actualiza el objeto de datos con los valores del array
		this.object = {};
		for (let item of array) {
			this.object[item[this.primary_key]] = item;
		}
	}

	/**
	 * @description Inicializa el objeto de datos y el array con una copia de la colección actual.
	 * @param {NOW.Collection} collection - La colección que se copiará.
	 */
	copy(collection) {
		this.object = Object.assign({}, collection.getObject());
		this.array = Array.from(collection.getArray());
	}

	/**
	 * @description Vuelve a cargar el array de datos desde la fuente original, manteniendo el objeto de datos actualizado.
	 * @returns {Promise<void>} - Promesa que se resuelve cuando la carga se completa.
	 */
	async reload() {
		await this.load(this.array);
	}
};
